import {
    AlertCircle,
    AlertTriangle,
    CheckCircle,
    Eye,
    File,
    FileText,
    FolderOpen,
    FolderPlus,
    Grid,
    Image as ImageIcon,
    List,
    RefreshCw,
    Search,
    Trash,
    Upload,
    Video,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

const MediaManagement = () => {
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState('');
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [gridView, setGridView] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [statistics, setStatistics] = useState(null);
    const [showDatabaseImages, setShowDatabaseImages] = useState(false);
    const [databaseImages, setDatabaseImages] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteModal, setDeleteModal] = useState({ show: false, item: null, type: null });

    // Fetch folders and statistics
    useEffect(() => {
        fetchFolders();
        fetchStatistics();
    }, []);

    // Fetch files when folder changes
    useEffect(() => {
        fetchFiles();
    }, [currentFolder, page, searchQuery]);

    const fetchFolders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/media/folders', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch folders');
            const data = await response.json();
            setFolders(data);
        } catch (error) {
            console.error('Error fetching folders:', error);
            setError('Failed to load folders');
        }
    };

    const fetchFiles = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            // Handle root folder case
            const folderPath = currentFolder === '' ? 'Root' : currentFolder;
            const response = await fetch(`/api/media/folder/${folderPath}?page=${page}&search=${searchQuery}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch files');
            const data = await response.json();
            setFiles(data);
            setTotalPages(Math.ceil(data.length / 20));
        } catch (error) {
            console.error('Error fetching files:', error);
            setError('Failed to load files');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/media/statistics', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch statistics');
            const data = await response.json();
            setStatistics(data);
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    const fetchDatabaseImages = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/media/database-images?page=${page}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch database images');
            const data = await response.json();
            setDatabaseImages([...data.uploads, ...data.mediaItems]);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            console.error('Error fetching database images:', error);
            setError('Failed to load database images');
        }
    };

    const handleCreateFolder = async (folderName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/media/folder', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ folderName })
            });
            if (!response.ok) throw new Error('Failed to create folder');
            fetchFolders();
        } catch (error) {
            console.error('Error creating folder:', error);
            setError('Failed to create folder');
        }
    };

    const handleDelete = async (fileName) => {
        setDeleteModal({
            show: true,
            item: fileName,
            type: 'file'
        });
    };

    const handleDeleteFolder = async (folderName) => {
        setDeleteModal({
            show: true,
            item: folderName,
            type: 'folder'
        });
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            let response;

            if (deleteModal.type === 'file') {
                response = await fetch(`/api/media/file/${currentFolder}/${deleteModal.item}`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                response = await fetch(`/api/media/folder/${deleteModal.item}`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (!response.ok) throw new Error(`Failed to delete ${deleteModal.type}`);

            setSuccess(`${deleteModal.type === 'file' ? 'File' : 'Folder'} deleted successfully!`);

            if (deleteModal.type === 'file') {
                fetchFiles();
            } else {
                fetchFolders();
                if (currentFolder === deleteModal.item) {
                    setCurrentFolder('');
                }
            }
        } catch (error) {
            console.error(`Error deleting ${deleteModal.type}:`, error);
            setError(`Failed to delete ${deleteModal.type}`);
        } finally {
            setDeleteModal({ show: false, item: null, type: null });
        }
    };

    const handleUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        try {
            const token = localStorage.getItem('token');
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `/api/media/upload/${currentFolder || 'Root'}`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentCompleted = Math.round((event.loaded * 100) / event.total);
                    setUploadProgress(percentCompleted);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    setSuccess(`Successfully uploaded ${files.length} file(s)!`);
                    fetchFiles();
                    fetchStatistics();
                } else {
                    setError('Upload failed');
                }
                setUploading(false);
            };

            xhr.onerror = () => {
                setError('Upload failed');
                setUploading(false);
            };

            xhr.send(formData);
        } catch (error) {
            console.error('Error uploading files:', error);
            setError('Upload failed');
            setUploading(false);
        }
    };

    const handleMoveFile = async (sourceFolder, targetFolder, fileName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/media/file/move', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sourceFolder, targetFolder, fileName })
            });
            if (!response.ok) throw new Error('Failed to move file');
            fetchFiles();
        } catch (error) {
            console.error('Error moving file:', error);
            setError('Failed to move file');
        }
    };

    const getFileIcon = (file) => {
        const ext = file.name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
            return <ImageIcon className="w-6 h-6 text-blue-500" />;
        } else if (['mp4', 'mov', 'avi', 'wmv'].includes(ext)) {
            return <Video className="w-6 h-6 text-red-500" />;
        } else if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
            return <FileText className="w-6 h-6 text-green-500" />;
        }
        return <File className="w-6 h-6 text-gray-500" />;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileUrl = (file) => {
        if (currentFolder === '') {
            return `/api/upload/${file.name}`;
        } else {
            return `/api/upload/${currentFolder}/${file.name}`;
        }
    };

    const renderFilePreview = (file) => {
        const fileUrl = getFileUrl(file);

        if (file.mimetype && file.mimetype.startsWith('image/')) {
            return (
                <img
                    src={fileUrl}
                    alt={file.name}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
            );
        } else if (file.mimetype && file.mimetype.startsWith('video/')) {
            return (
                <video
                    src={fileUrl}
                    className="w-full h-48 object-cover rounded-lg"
                    controls
                    preload="metadata"
                />
            );
        } else {
            return (
                <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                    {getFileIcon(file)}
                    <span className="ml-2 text-sm text-gray-600">{file.name}</span>
                </div>
            );
        }
    };

    // Custom Alert Component
    const Alert = ({ type, message, onClose }) => {
        const alertStyles = {
            success: 'bg-green-50 border-green-200 text-green-800',
            error: 'bg-red-50 border-red-200 text-red-800',
            warning: 'bg-yellow-50 border-yellow-200 text-yellow-800'
        };

        const iconStyles = {
            success: 'text-green-400',
            error: 'text-red-400',
            warning: 'text-yellow-400'
        };

        const AlertIcon = type === 'success' ? CheckCircle : type === 'error' ? AlertCircle : AlertTriangle;

        return (
            <div className={`fixed top-4 right-4 z-50 max-w-md w-full mx-auto`}>
                <div className={`border rounded-lg p-4 shadow-lg ${alertStyles[type]} animate-in slide-in-from-top-2 duration-300`}>
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <AlertIcon className={`h-5 w-5 ${iconStyles[type]}`} />
                        </div>
                        <div className="ml-3 flex-1">
                            <p className="text-sm font-medium">{message}</p>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                            <button
                                onClick={onClose}
                                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${type === 'success' ? 'text-green-500 hover:bg-green-100 focus:ring-green-600' :
                                    type === 'error' ? 'text-red-500 hover:bg-red-100 focus:ring-red-600' :
                                        'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600'
                                    }`}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Delete Confirmation Modal
    const DeleteModal = () => {
        if (!deleteModal.show) return null;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-screen items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                        onClick={() => setDeleteModal({ show: false, item: null, type: null })}
                    />

                    {/* Modal */}
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto transform transition-all">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <AlertTriangle className="w-6 h-6 text-red-600" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Delete {deleteModal.type === 'file' ? 'File' : 'Folder'}
                                    </h3>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to delete <span className="font-medium text-gray-900">"{deleteModal.item}"</span>?
                                    {deleteModal.type === 'folder' && ' This will delete all files in the folder.'} This action cannot be undone.
                                </p>
                            </div>

                            <div className="flex space-x-3 justify-end">
                                <button
                                    onClick={() => setDeleteModal({ show: false, item: null, type: null })}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 p-6">
            {/* Alerts */}
            {error && (
                <Alert
                    type="error"
                    message={error}
                    onClose={() => setError(null)}
                />
            )}
            {success && (
                <Alert
                    type="success"
                    message={success}
                    onClose={() => setSuccess(null)}
                />
            )}

            {/* Delete Modal */}
            <DeleteModal />

            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Media Management</h2>
                <div className="flex gap-2">
                    <button
                        onClick={() => setGridView(true)}
                        className={`p-2 rounded ${gridView ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        title="Grid View"
                    >
                        <Grid size={18} />
                    </button>
                    <button
                        onClick={() => setGridView(false)}
                        className={`p-2 rounded ${!gridView ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        title="List View"
                    >
                        <List size={18} />
                    </button>
                </div>
            </div>

            {/* Statistics */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-4 rounded-lg shadow">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">Total Files</h3>
                        <p className="text-2xl">{statistics.totalFiles}</p>
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">Storage Used</h3>
                        <p className="text-2xl">{statistics.storage.formatted}</p>
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">Images</h3>
                        <p className="text-2xl">{statistics.fileTypes.images}</p>
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">Videos</h3>
                        <p className="text-2xl">{statistics.fileTypes.videos}</p>
                    </div>
                </div>
            )}

            {/* Search and Actions */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="relative flex-grow max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search files..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="flex gap-2">
                    <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md flex items-center gap-2 transition-colors">
                        <Upload size={18} />
                        Upload Files
                        <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleUpload}
                        />
                    </label>

                    <button
                        onClick={() => setShowDatabaseImages(!showDatabaseImages)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                    >
                        <FileText size={18} />
                        {showDatabaseImages ? 'Show Files' : 'Show Database Images'}
                    </button>

                    <button
                        onClick={fetchFiles}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                    >
                        <RefreshCw size={18} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <p className="text-sm text-gray-500 mt-1">
                        Uploading: {uploadProgress}%
                    </p>
                </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Folders Sidebar */}
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Folders</h3>
                        <button
                            onClick={() => {
                                const folderName = prompt('Enter folder name:');
                                if (folderName) handleCreateFolder(folderName);
                            }}
                            className="text-blue-500 hover:text-blue-700"
                        >
                            <FolderPlus size={18} />
                        </button>
                    </div>
                    <div className="space-y-2">
                        <button
                            onClick={() => setCurrentFolder('')}
                            className={`w-full text-left px-2 py-1 rounded ${currentFolder === '' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                                }`}
                        >
                            <FolderOpen className="inline-block mr-2" size={16} />
                            Root
                        </button>
                        {folders.map((folder) => (
                            <button
                                key={folder.name}
                                onClick={() => setCurrentFolder(folder.name)}
                                className={`w-full text-left px-2 py-1 rounded ${currentFolder === folder.name ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                                    }`}
                            >
                                <FolderOpen className="inline-block mr-2" size={16} />
                                {folder.name}
                                <span className="text-xs text-gray-500 ml-2">
                                    ({folder.fileCount} files)
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Files Display */}
                <div className="md:col-span-3">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                        </div>
                    ) : showDatabaseImages ? (
                        // Database Images Display
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-4 border-b">
                                <h3 className="text-lg font-semibold">Database Images</h3>
                            </div>
                            <div className="p-4">
                                {databaseImages.length === 0 ? (
                                    <p className="text-gray-500 text-center">No database images found</p>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {databaseImages.map((image) => (
                                            <div key={image._id} className="relative group">
                                                <img
                                                    src={image.data ? `data:${image.mimeType};base64,${image.data}` : image.path}
                                                    alt={image.name}
                                                    className="w-full h-48 object-cover rounded-lg"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                                                    <button
                                                        onClick={() => setSelectedFile(image)}
                                                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(image._id)}
                                                        className="p-2 bg-white rounded-full hover:bg-gray-100 text-red-600"
                                                    >
                                                        <Trash size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // File System Display
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-4 border-b">
                                <h3 className="text-lg font-semibold">
                                    {currentFolder === '' ? 'Root Folder' : `Folder: ${currentFolder}`}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {files.length} files
                                </p>
                            </div>
                            {files.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                    <p>No files in this folder</p>
                                </div>
                            ) : gridView ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                                    {files.map((file) => (
                                        <div key={file.name} className="relative group">
                                            {renderFilePreview(file)}
                                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                                                <button
                                                    onClick={() => setSelectedFile(file)}
                                                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(file.name)}
                                                    className="p-2 bg-white rounded-full hover:bg-gray-100 text-red-600"
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 truncate">
                                                {file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {files.map((file) => (
                                                <tr key={file.name}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="w-16 h-16">
                                                            {file.mimetype && file.mimetype.startsWith('image/') ? (
                                                                <img
                                                                    src={getFileUrl(file)}
                                                                    alt={file.name}
                                                                    className="w-16 h-16 object-cover rounded"
                                                                />
                                                            ) : (
                                                                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                                                                    {getFileIcon(file)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className="text-sm font-medium text-gray-900">{file.name}</span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className="text-sm text-gray-500">{file.mimetype || 'Unknown'}</span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className="text-sm text-gray-500">{formatFileSize(file.size)}</span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className="text-sm text-gray-500">
                                                            {new Date(file.lastModified).toLocaleString()}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => setSelectedFile(file)}
                                                                className="text-blue-600 hover:text-blue-900"
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(file.name)}
                                                                className="text-red-600 hover:text-red-900"
                                                            >
                                                                <Trash size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* File Preview Modal */}
            {selectedFile && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-4xl w-full">
                        <div className="p-4">
                            <h3 className="text-lg font-semibold mb-4">{selectedFile.name}</h3>
                            {selectedFile.metadata?.type?.startsWith('image/') ? (
                                <img
                                    src={`/api/media/file/${currentFolder}/${selectedFile.name}`}
                                    alt={selectedFile.name}
                                    className="w-full h-auto max-h-[70vh] object-contain"
                                />
                            ) : (
                                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                                    {getFileIcon(selectedFile)}
                                </div>
                            )}
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                <div>
                                    <p><strong>Type:</strong> {selectedFile.metadata?.type || 'Unknown'}</p>
                                    <p><strong>Size:</strong> {formatFileSize(selectedFile.size)}</p>
                                </div>
                                <div>
                                    <p><strong>Modified:</strong> {new Date(selectedFile.lastModified).toLocaleString()}</p>
                                    {selectedFile.metadata?.dimensions && (
                                        <p><strong>Dimensions:</strong> {selectedFile.metadata.dimensions}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t flex justify-end space-x-2">
                            <button
                                onClick={() => handleDelete(selectedFile.name)}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setSelectedFile(null)}
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaManagement; 